import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en';
import es from './es';

export type SupportedLanguage = 'en' | 'es';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'es'];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Persist an explicit user choice so a refresh keeps the selected language.
const LANGUAGE_STORAGE_KEY = 'neoemr.portal.lang';

/**
 * Normalize any locale-ish value to a supported portal language.
 * LATAM-first: any `es*` locale (es, es-419, es-MX, es-CO, ...) maps to `es`.
 */
export function normalizeLanguage(value?: string | null): SupportedLanguage | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('en')) return 'en';
  return null;
}

function readStoredLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Detect the initial language before the authenticated profile is available.
 * Order: explicit stored choice -> navigator.language -> default (en).
 * Once the user's `preferredLanguage` loads, call applyPreferredLanguage().
 */
export function detectInitialLanguage(): SupportedLanguage {
  const stored = readStoredLanguage();
  if (stored) return stored;
  const navLanguage =
    typeof navigator !== 'undefined' ? normalizeLanguage(navigator.language) : null;
  return navLanguage || DEFAULT_LANGUAGE;
}

/** Persist + apply an explicit user language choice (e.g. from Settings). */
export function setPortalLanguage(language: SupportedLanguage) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
  if (i18n.language !== language) {
    void i18n.changeLanguage(language);
  }
}

/**
 * Apply the authenticated user's preferredLanguage when no explicit choice has
 * been stored yet. An explicit Settings choice always wins over the profile.
 */
export function applyPreferredLanguage(preferredLanguage?: string | null) {
  if (readStoredLanguage()) return;
  const resolved = normalizeLanguage(preferredLanguage);
  if (resolved && i18n.language !== resolved) {
    void i18n.changeLanguage(resolved);
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
  },
  lng: detectInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
});

export default i18n;
